import { useEffect, useState } from 'react';
import { ImagePlus, Loader2 } from 'lucide-react';
import { Button } from './button';
import { FileInput } from './file-input';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from './dialog';
import { ImageCropper, type ImageCropCoords } from './image-cropper';
import { RASTER_IMAGE_ACCEPT, RASTER_IMAGE_LABEL } from '../../lib/file-types';

export type { ImageCropCoords } from './image-cropper';

export interface ImageCropResult {
	/** The cropped image blob */
	croppedBlob: Blob;
	/** Object URL for immediate preview */
	previewUrl: string;
	/** The original file the user selected */
	originalFile: File;
}

interface ImageCropModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** Blob mode: bake and return a cropped image blob (logos/avatars). */
	onSave?: (result: ImageCropResult) => void;
	/** Coordinates mode: emit the crop rect (percentages) for display-time crop. */
	onSaveCrop?: (coords: ImageCropCoords) => void;
	/** Modal title when uploading. Default: "Upload image" */
	title?: string;
	/** Modal title when cropping. Default: "Crop image" */
	cropTitle?: string;
	/** Aspect ratio of the crop area. Default: 1 (square) */
	aspect?: number;
	/** Shape of the crop area. Default: "rect" */
	cropShape?: 'rect' | 'round';
	/** Size of the crop area in pixels. Default: 260 */
	cropSize?: number;
	/** Label for the save button. Default: "Save" */
	saveLabel?: string;
	/** URL of an existing image to pre-load for re-cropping */
	initialImage?: string | null;
	/** Restore a previously saved crop (coordinates mode). */
	initialCrop?: ImageCropCoords | null;
	/** Hide the rotate control. */
	hideRotate?: boolean;
	/** Keep the crop area within the image bounds. Default: false. */
	restrictPosition?: boolean;
	/** External busy flag — disables the save button. */
	saving?: boolean;
}

/**
 * Dialog wrapper around {@link ImageCropper}. Handles the "no image yet →
 * choose a file" phase and pre-loading an existing image, then hands a concrete
 * source to the cropper. For cropping inside another surface (avoiding a
 * modal-on-modal stack), render {@link ImageCropper} directly instead.
 */
export function ImageCropModal({
	open,
	onOpenChange,
	onSave,
	onSaveCrop,
	title = 'Upload image',
	cropTitle = 'Crop image',
	aspect = 1,
	cropShape = 'rect',
	cropSize = 260,
	saveLabel = 'Save',
	initialImage,
	initialCrop,
	hideRotate = false,
	restrictPosition = false,
	saving = false,
}: ImageCropModalProps) {
	const coordinatesMode = !!onSaveCrop;
	const [imageSrc, setImageSrc] = useState<string | null>(null);
	const [originalFile, setOriginalFile] = useState<File | null>(null);
	const [initialLoading, setInitialLoading] = useState(false);

	const reset = () => {
		setImageSrc(null);
		setOriginalFile(null);
		setInitialLoading(false);
	};

	/* ── Pre-load existing image for re-cropping ── */
	useEffect(() => {
		if (!open || !initialImage || imageSrc) return;

		/* Coordinates mode never bakes pixels, so it can point the cropper
		 * straight at the URL — no blob fetch, and no CORS requirement. */
		if (coordinatesMode) {
			setImageSrc(initialImage);
			return;
		}

		setInitialLoading(true);
		fetch(initialImage)
			.then((res) => res.blob())
			.then((blob) => {
				const ext = blob.type.split('/')[1] || 'png';
				const file = new File([blob], `original.${ext}`, { type: blob.type });
				setOriginalFile(file);
				const reader = new FileReader();
				reader.onload = (ev) => {
					setImageSrc(ev.target?.result as string);
					setInitialLoading(false);
				};
				reader.readAsDataURL(blob);
			})
			.catch(() => setInitialLoading(false));
	}, [open, initialImage, imageSrc, coordinatesMode]);

	const handleFileSelect = (file: File) => {
		setOriginalFile(file);
		const reader = new FileReader();
		reader.onload = (ev) => setImageSrc(ev.target?.result as string);
		reader.readAsDataURL(file);
	};

	return (
		<Dialog
			open={open}
			onOpenChange={(next) => {
				if (!next) reset();
				onOpenChange(next);
			}}
		>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>{imageSrc ? cropTitle : title}</DialogTitle>
					<DialogDescription>
						{imageSrc
							? 'Drag to reposition, scroll to zoom.'
							: 'Choose an image to get started.'}
					</DialogDescription>
				</DialogHeader>

				<FileInput
					onFiles={(files) => {
						const f = files[0];
						if (f) handleFileSelect(f);
					}}
					accept={RASTER_IMAGE_ACCEPT}
				>
					{({ open: openPicker, converting }) =>
						!imageSrc ? (
							<>
								<DialogBody>
									{initialLoading ? (
										<div className="flex flex-col items-center gap-3 py-12 text-text-3">
											<Loader2 size={24} className="animate-spin" />
											<div className="text-base">Loading image…</div>
										</div>
									) : (
										<button
											type="button"
											onClick={openPicker}
											disabled={converting}
											className="flex w-full cursor-pointer flex-col items-center gap-3 rounded-xl border-2 border-dashed border-line-2 py-12 text-text-3 transition-colors hover:border-line-3 hover:bg-surface-hover hover:text-text-2"
										>
											<div className="flex h-12 w-12 items-center justify-center rounded-full border border-line-1 bg-surface-fill">
												<ImagePlus size={20} />
											</div>
											<div>
												<div className="text-base font-medium">
													{converting ? 'Converting…' : 'Choose an image'}
												</div>
												<div className="mt-0.5 text-sm text-text-4">
													{RASTER_IMAGE_LABEL}
												</div>
											</div>
										</button>
									)}
								</DialogBody>
								<DialogFooter showCloseButton />
							</>
						) : (
							<ImageCropper
								key={imageSrc}
								imageSrc={imageSrc}
								aspect={aspect}
								cropShape={cropShape}
								cropSize={cropSize}
								saveLabel={saveLabel}
								initialCrop={initialCrop}
								hideRotate={hideRotate}
								restrictPosition={restrictPosition}
								saving={saving}
								onCancel={() => onOpenChange(false)}
								secondaryAction={
									coordinatesMode ? undefined : (
										<Button
											type="button"
											variant="outline"
											onClick={openPicker}
											disabled={converting}
										>
											{converting ? 'Converting…' : 'Change image'}
										</Button>
									)
								}
								onSaveCrop={
									coordinatesMode ? (coords) => onSaveCrop!(coords) : undefined
								}
								onSaveBlob={
									!coordinatesMode
										? ({ croppedBlob, previewUrl }) => {
												if (originalFile)
													onSave?.({
														croppedBlob,
														previewUrl,
														originalFile,
													});
												onOpenChange(false);
											}
										: undefined
								}
							/>
						)
					}
				</FileInput>
			</DialogContent>
		</Dialog>
	);
}
